import React, { forwardRef, useCallback } from 'react';
import { css, cx } from '@emotion/css';
import { dateToToLongMonthString, getDayAbbreviations, getDateFromDateOrNow, HoverState, SelectedState, } from '../DateModules/shared';
import { CalendarBackButton, CalendarForwardButton, DayHeader, Month, MonthHeader, Week, } from '../DateModules/Calendar';
import { theme, useLocalizationContext, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import { useDatePicker } from '../DateModules/useDatePicker';
import { useKeyboardAccesibility } from '../DateModules/useKeyboardAccesibility';
export const validateDate = ({ minDate, maxDate, date, }) => {
    if (minDate && maxDate && getDateFromDateOrNow(minDate) >= getDateFromDateOrNow(maxDate)) {
        console.warn(`minDate must be less than maxDate`);
    }
    if (date) {
        if (minDate && getDateFromDateOrNow(minDate) > date) {
            console.warn(`date must be greater than or equal to minDate`);
        }
        if (maxDate && getDateFromDateOrNow(maxDate) < date) {
            console.warn(`date must be less than or equal to maxDate`);
        }
    }
};
export const datePickerCSS = (numberOfMonths, selectedTheme) => css `
  background-color: ${getThemeColor(selectedTheme, { light: theme.color.white, dark: theme.color.black })};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  width: ${numberOfMonths * 334}px;
  line-height: initial;
  padding: 8px;
  border: 0;
`;
export const getSelectedState = ({ selected: isSelected }) => (isSelected ? SelectedState.SELECTED : undefined);
export const DatePicker = forwardRef(({ date, minDate, maxDate, initialMonth = 'NOW', testId = 'date-picker', className, onDateSelected, ...rest }, ref) => {
    validateDate({ minDate, maxDate, date });
    const { selectedTheme } = useThemeContext();
    const { locale } = useLocalizationContext();
    const handleDateSelected = useCallback(({ date: newDate, selectable }) => {
        if (selectable) {
            onDateSelected(newDate);
        }
    }, [onDateSelected]);
    const selected = date ? [date] : [];
    const { calendars, getDateProps, getBackProps, getForwardProps, hoveredDate, onDateMouseOver, onDateMouseOut } = useDatePicker({
        date: date || getDateFromDateOrNow(initialMonth),
        minDate,
        maxDate,
        monthsToDisplay: 1,
        selected,
        onDateSelected: handleDateSelected,
    });
    const { onKeyDown: onAccesibleKeyDown } = useKeyboardAccesibility({
        handleChange: onDateSelected,
        currentValue: date ?? new Date(),
        minDate,
        maxDate,
    });
    const calendar = calendars[0];
    const handleKeyDown = (event) => {
        onAccesibleKeyDown(event);
    };
    return (React.createElement("div", { ...rest, role: "group", onKeyDown: handleKeyDown, "data-testid": testId, ref: ref, className: cx(className, datePickerCSS(1, selectedTheme), 'ds-date-picker') },
        React.createElement("div", { className: css `
                  display: flex;
                  position: relative;
                ` },
            React.createElement(CalendarBackButton, { getButtonProps: (props) => getBackProps({ ...props, calendars }) }),
            React.createElement(CalendarForwardButton, { getButtonProps: (props) => getForwardProps({ ...props, calendars }) }),
            React.createElement(Month, { key: `${calendar.month}-${calendar.year}` },
                React.createElement(MonthHeader, { month: dateToToLongMonthString(calendar.firstDayOfMonth, locale), year: calendar.year.toString() }),
                React.createElement(DayHeader, { dayAbbreviations: getDayAbbreviations(locale) }),
                calendar.weeks.map((week, weekIndex) => {
                    const key = `${calendar.year}-${calendar.month}-${weekIndex}`;
                    return (React.createElement(Week, { key: key, week: week, getDateProps: getDateProps, getSelectedState: getSelectedState, getHoverState: ({ date: dateToCheck }) => hoveredDate && dateToCheck.getTime() === hoveredDate.getTime() ? HoverState.HOVERED : undefined, onDateMouseOver: onDateMouseOver, onDateMouseOut: onDateMouseOut }));
                })))));
});
DatePicker.displayName = 'DatePicker';
